import api from '@/services/api';

const BASE = '/v2/person';

export interface PersonProfile {
    firstName?: string;
    lastName?: string;
    name?: string;
    email?: string;
    phone?: string;
    title?: string;
    company?: string;
    location?: string;
    avatarUrl?: string;
    linkedInUrl?: string;
    websiteUrl?: string;
}

export interface ContactMethod {
    type: 'email' | 'phone' | 'whatsapp' | 'other';
    value: string;
    label: string;
    preferred: boolean;
}

export interface PersonDocument {
    _id: string;
    type: 'cv' | 'cover_letter' | 'portfolio' | 'certificate' | 'identity' | 'other';
    fileKey: string;
    originalName?: string;
    fileSize?: number;
    text?: string;
    uploadedAt: string;
    version: number;
}

export interface PersonNote {
    _id: string;
    content: string;
    createdBy?: string;
    createdAt: string;
}

export interface Person {
    _id: string;
    organizationId: string;
    authIdentityId?: string;
    profile: PersonProfile;
    contactMethods: ContactMethod[];
    documents: PersonDocument[];
    customFields: Record<string, unknown>;
    tags: string[];
    notes: PersonNote[];
    source: string;
    sourceDetails?: string;
    mergedFromIds: string[];
    createdAt: string;
    updatedAt: string;
    authIdentity?: {
        identifiers: { type: string; value: string; verified: boolean; primary: boolean }[];
        profile: { firstName?: string; lastName?: string; name?: string };
    };
}

export interface PersonListResult {
    results: Person[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface CustomFieldDefinition {
    _id: string;
    organizationId: string;
    workspaceId: string | null;
    syncToPerson: boolean;
    key: string;
    label: string;
    type: string;
    options: string[];
    isSearchable: boolean;
    isRequired: boolean;
    description: string;
    order: number;
    createdAt: string;
    updatedAt: string;
}

export const personApi = {
    // ─── Person CRUD ────────────────────────────────────────────────────────

    listPersons: (params?: { search?: string; tags?: string; source?: string; page?: number; limit?: number; sort?: string }) =>
        api.get<PersonListResult>(BASE, { params }),

    getPerson: (id: string) =>
        api.get<Person>(`${BASE}/${id}`),

    createPerson: (data: {
        profile: PersonProfile;
        identifier?: { type: 'email' | 'phone'; value: string };
        contactMethods?: ContactMethod[];
        tags?: string[];
        source?: string;
        sourceDetails?: string;
        customFields?: Record<string, unknown>;
        note?: string;
    }) => api.post<Person>(BASE, data),

    updatePerson: (id: string, data: {
        profile?: PersonProfile;
        contactMethods?: ContactMethod[];
        tags?: string[];
        customFields?: Record<string, unknown>;
        source?: string;
        sourceDetails?: string;
    }) => api.patch<Person>(`${BASE}/${id}`, data),

    deletePerson: (id: string) =>
        api.delete(`${BASE}/${id}`),

    // ─── Notes ──────────────────────────────────────────────────────────────

    addNote: (id: string, content: string) =>
        api.post<PersonNote[]>(`${BASE}/${id}/notes`, { content }),

    // ─── Documents ──────────────────────────────────────────────────────────

    addDocument: (id: string, data: {
        type: string;
        fileKey: string;
        originalName?: string;
        fileSize?: number;
        text?: string;
    }) => api.post<PersonDocument[]>(`${BASE}/${id}/documents`, data),

    // ─── Merge ──────────────────────────────────────────────────────────────

    mergePersons: (survivorId: string, loserId: string) =>
        api.post<Person>(`${BASE}/merge`, { survivorId, loserId }),

    // ─── Custom Field Definitions ───────────────────────────────────────────

    listCustomFields: (params?: { syncToPerson?: boolean }) =>
        api.get<CustomFieldDefinition[]>(`${BASE}/custom-fields`, { params }),

    createCustomField: (data: {
        key: string;
        label: string;
        type: string;
        options?: string[];
        isSearchable?: boolean;
        isRequired?: boolean;
        syncToPerson?: boolean;
        description?: string;
        order?: number;
        workspaceId?: string | null;
    }) => api.post<CustomFieldDefinition>(`${BASE}/custom-fields`, data),

    updateCustomField: (id: string, data: {
        label?: string;
        description?: string;
        options?: string[];
        isSearchable?: boolean;
        isRequired?: boolean;
        syncToPerson?: boolean;
        order?: number;
    }) => api.patch<CustomFieldDefinition>(`${BASE}/custom-fields/${id}`, data),

    deleteCustomField: (id: string) =>
        api.delete(`${BASE}/custom-fields/${id}`),
};
